import cd from 'debug';
import { findKey, includes, throttle } from 'lodash';

const debug = cd('app:services:tracking-manager');

export enum Target {
    MainbetAdd = 'mainbet_add',
    LivebetAdd = 'livebet_add',
    FooterbetAdd = 'footerbet_add',
    GamesContainerMarket = 'games_container_market',
    AcceptBet = 'accept_bet',
    NotacceptBet = 'notaccept_bet',
    OddsUpdates = 'odds_updates',
    DeleteAll = 'delete_all',
    Minimize = 'minimize',
    MakeBet = 'make_bet',
    TheaterModeVideoOpen = 'theatermode_video_open',
    TheaterModeVideoClose = 'theatermode_video_close',
    TheaterModeMatchOpen = 'theatermode_match_open',
    TheaterModeMatchClose = 'theatermode_match_close',
    $BetAdd = 'bet_add',
}

enum Origin {
    Main = 'main',
    Live = 'live',
    Footer = 'footer',
}

type EventWithPath = Event & {
    path?: EventTarget[];
};

class TrackingManager {
    public registerUserAction(target: string): void {
        if (!window.dataLayer) {
            window.dataLayer = [];
        }

        if (typeof window.dataLayer.push === 'function') {
            window.dataLayer.push({ event: target });
            debug('update dataLayer', window.dataLayer);
        }
    }

    public markTarget(target: Target | null) {
        if (!target) return {};

        return { 'data-analytics-info': target };
    }

    public markOrigin(origin: Origin): { 'data-analytics-origin': Origin } {
        return { 'data-analytics-origin': origin };
    }

    private getEventPath(e: EventWithPath): HTMLElement[] {
        const path = (e.composedPath && e.composedPath()) || e.path;
        const { target } = e;

        if (path != null) {
            return (path.indexOf(window) < 0
                ? path.concat(window)
                : path) as HTMLElement[];
        }

        if (target === window) {
            return [window] as any[];
        }

        function getParents(
            node: EventTarget | null,
            memo: EventTarget[] | null
        ): EventTarget[] {
            const memoResult = memo || [];
            const { parentNode } = <HTMLElement>node;

            if (!parentNode) {
                return memoResult;
            }

            return getParents(parentNode, memoResult.concat(parentNode));
        }

        return [target].concat(
            getParents(target, null),
            window
        ) as HTMLElement[];
    }

    private handleUserClick = (e: Event) => {
        debug('handle event', e);

        let originInfo = '';
        const ePath = this.getEventPath(e);

        ePath.reverse().forEach(element => {
            if (!element || !element.dataset) {
                return;
            }

            if (element.dataset.analyticsOrigin) {
                originInfo = element.dataset.analyticsOrigin;
            }

            const attribute = element.dataset.analyticsInfo;

            if (attribute) {
                const targetKey = findKey(Target, value => value === attribute);

                if (targetKey && targetKey.includes('$') && originInfo) {
                    this.registerUserAction(originInfo + attribute);
                } else if (includes(Target, attribute)) {
                    this.registerUserAction(attribute);
                }
            }
        });
    };

    private throttledUserClickHandler = throttle(this.handleUserClick, 300, {
        trailing: false,
    });

    public init() {
        document.addEventListener('mousedown', this.throttledUserClickHandler);
        document.addEventListener('touchstart', this.throttledUserClickHandler);
    }

    public cleanUp() {
        document.removeEventListener(
            'mousedown',
            this.throttledUserClickHandler
        );
        document.removeEventListener(
            'touchstart',
            this.throttledUserClickHandler
        );
    }
}

let trackingManagerInstance: TrackingManager | null = null;

function getTrackingManager(): TrackingManager {
    if (!trackingManagerInstance) {
        trackingManagerInstance = new TrackingManager();
    }

    return trackingManagerInstance;
}

export { getTrackingManager as tracker };
